import { computed, DeepReadonly } from 'vue';
import { useI18n } from 'vue-i18n';
import { Transcription } from '@/api/openapi';

type TSpeakerType = 'receptionist' | 'insideSales' | 'customer' | 'unknown';

export const SPEAKER_COLORS = {
  'receptionist': '#969696',
  'insideSales': '#2CBFB7',
  'customer': '#9EA7DF',
  'unknown': '#E3E3E3',
};

const useTranscriptionSpeakers = (getTranscription: () => DeepReadonly<Transcription> | null) => {
  const { t } = useI18n();

  const SPEAKER_LABEL = {
    'insideSales': t('transcription.speakerLabel.insideSales'),
  };

  const speakerPresumption = computed(() => {
    return getTranscription()?.complementedSpeakerPresumption;
  });

  const computeColorBySpeaker = (speaker: string | null): string => {
    const speakerType = computeSpeakerType(speaker);
    return SPEAKER_COLORS[speakerType];
  };

  const computeSpeakerType = (speaker: string | null): TSpeakerType => {
    if (speaker === speakerPresumption.value?.receptionist) {
      return 'receptionist';
    }
    if (speaker === speakerPresumption.value?.insideSales) {
      return 'insideSales';
    }
    if (speaker === speakerPresumption.value?.customer) {
      return 'customer';
    }
    return 'unknown';
  };

  const findInsideSalesSpeaker = () => {
    return speakerPresumption.value?.insideSales;
  };

  const findCustomerSpeaker = () => {
    return speakerPresumption.value?.customer;
  };

  const computeSpeakerName = (speaker: string) => {
    if (speaker == null) {
      return `話者${maxSpeakerNumber.value + 1}`;
    }

    const speakerType = computeSpeakerType(speaker);
    if (speakerType === 'insideSales') {
      return SPEAKER_LABEL[speakerType];
    }

    const speakerMatch = /^SPEAKER_(\d+)$/.exec(speaker ?? '');
    if (speakerMatch) {
      const speakerNumber = parseInt(speakerMatch[1], 10) + 1;
      return `話者${speakerNumber}`;
    }

    return speaker;
  };

  const allSpeakers = computed(() => {
    return getTranscription()?.mergedTexts?.map((text) => text.speaker) ?? [];
  });

  const maxSpeakerNumber = computed(() => {
    // NOTE: 以下より、 `speakerPresumption` からでなく、実際のすべての話者から算出する。
    //       - `speakerPresumption（complementedSpeakerPresumption）` にすべての話者が含まれているとは限らない。
    // NOTE: `SPEAKER_NN` の数値が通し番号でない場合を一応考慮して、ユーザー数からでなく、 `SPEAKER_NN` の数値部分から算出する。
    return Math.max(...allSpeakers.value.map((speaker) => {
      const speakerMatch = /^SPEAKER_(\d+)$/.exec(speaker ?? '');
      if (speakerMatch) {
        return parseInt(speakerMatch[1], 10) + 1;
      }
      return 0;
    }));
  });

  return {
    computeColorBySpeaker,
    findInsideSalesSpeaker,
    findCustomerSpeaker,
    computeSpeakerName,
  };
};

export { useTranscriptionSpeakers };
